/**
 * Machine-readable error envelope + stable error-code enum (PRD §8 API conventions).
 *
 * Contract (PRD §8):
 * - Every error response uses ONE envelope: `{ code, message, fieldErrors? }`.
 * - `code` is a STABLE machine string — clients branch on it (e.g. the three
 *   distinct 429 codes RATE_LIMITED / STREAM_LIMIT / EGRESS_QUOTA, AC 5.5).
 * - `message` is ALWAYS a safe, generic, user-presentable string. Internal
 *   detail (stack traces, DB/Drive errors, file paths) NEVER appears in it —
 *   it goes to structured logs only, correlated by the per-request id.
 *
 * Enforcement in this module (not just convention):
 * - `AppError.message` is always drawn from the fixed {@link SAFE_ERROR_MESSAGES}
 *   table keyed by code — there is no free-form message parameter at all.
 * - Internal detail travels on a dedicated, NON-serialized channel
 *   (`internalDetail` — an ECMAScript private field exposed via getter for
 *   loggers; `cause` per the standard Error option). `JSON.stringify(appError)`
 *   yields exactly the envelope via `toJSON()` and can never leak either.
 */
import { z } from 'zod';
/** Stable machine-readable error codes (PRD §8 status-code semantics). */
export const ERROR_CODES = {
    /** 401 — no authenticated session. */
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    /** 403 — authenticated but not allowed (allowlist / role / CSRF). */
    FORBIDDEN: 'FORBIDDEN',
    /** 404 — unknown / never-existed resource (also cross-user isolation: no existence disclosure). */
    NOT_FOUND: 'NOT_FOUND',
    /** 410 — existed but is now `available=false` (removed from Drive); clients must not retry. */
    GONE: 'GONE',
    /**
     * 409 — the caller's view of a per-user resource is STALE (Story 6): a queue
     * mutation carried a `version` token that no longer matches the server's
     * `queue_state.updated_at`-derived one, so applying it would silently clobber
     * a write that landed from another device.
     *
     * Deliberately a DISTINCT code rather than a 422: nothing about the request
     * is malformed, the caller simply lost a Last-Write-Wins race, and the
     * remedy is mechanical — re-read `GET /api/queue/reconcile` and replay. A
     * client can branch on that without string-matching a validation message.
     */
    CONFLICT: 'CONFLICT',
    /** 422 — request failed validation; details in `fieldErrors`. */
    VALIDATION: 'VALIDATION',
    /** 413 — request body over a hard cap (e.g. telemetry batch > 50 events). */
    PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
    /** 429 — per-user / per-IP request-rate cap; carries `Retry-After` seconds. */
    RATE_LIMITED: 'RATE_LIMITED',
    /** 429 — per-user concurrent-stream cap (≤ 2/user, ≤ 10 system-wide). */
    STREAM_LIMIT: 'STREAM_LIMIT',
    /** 429 — per-user 10 GB/day egress cap; `Retry-After` until quota reset. */
    EGRESS_QUOTA: 'EGRESS_QUOTA',
    /** 500 — unexpected server error; internal detail is in logs only. */
    INTERNAL: 'INTERNAL',
};
/** Default HTTP status per code (PRD §8 status-code semantics). */
export const ERROR_HTTP_STATUS = {
    UNAUTHENTICATED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    GONE: 410,
    CONFLICT: 409,
    VALIDATION: 422,
    PAYLOAD_TOO_LARGE: 413,
    RATE_LIMITED: 429,
    STREAM_LIMIT: 429,
    EGRESS_QUOTA: 429,
    INTERNAL: 500,
};
/**
 * The ONLY source of envelope `message` strings — safe, generic,
 * user-presentable. Per-surface UI copy (toast vs. player banner, §10.2/§10.6
 * of the UI contract) is rendered client-side keyed on `code`, never taken
 * from here verbatim, so these stay deliberately neutral.
 */
export const SAFE_ERROR_MESSAGES = {
    UNAUTHENTICATED: 'You need to sign in to do that.',
    FORBIDDEN: 'You are not allowed to do that.',
    NOT_FOUND: 'That resource does not exist.',
    GONE: 'This item is no longer available.',
    CONFLICT: 'This changed on another device. Reload and try again.',
    VALIDATION: 'Some fields are invalid.',
    PAYLOAD_TOO_LARGE: 'The request is too large.',
    RATE_LIMITED: 'Too many requests. Please slow down and retry shortly.',
    STREAM_LIMIT: 'Too many concurrent streams. A slot frees up when one ends.',
    EGRESS_QUOTA: 'Daily streaming quota exceeded. Streaming resumes after the quota resets.',
    INTERNAL: 'Something went wrong on our side. Please try again.',
};
export const errorCodeSchema = z.enum(ERROR_CODES);
/**
 * The wire envelope. `strictObject` so a round-trip is exact: unknown keys
 * (i.e. anything accidentally leaked alongside the contract) fail parsing.
 */
export const errorEnvelopeSchema = z.strictObject({
    code: errorCodeSchema,
    message: z.string().min(1),
    fieldErrors: z.record(z.string(), z.string()).optional(),
});
/**
 * Application error whose public `message` is provably generic: it is always
 * `SAFE_ERROR_MESSAGES[code]` — the constructor accepts no message at all.
 * Loggers read `internalDetail` / `cause`; serialization (`toEnvelope()` /
 * `toJSON()`) emits only `{ code, message, fieldErrors? }`.
 */
export class AppError extends Error {
    code;
    /** Default HTTP status for this code (PRD §8). */
    httpStatus;
    #fieldErrors;
    #internalDetail;
    constructor(code, options = {}) {
        if ('cause' in options) {
            super(SAFE_ERROR_MESSAGES[code], { cause: options.cause });
        }
        else {
            super(SAFE_ERROR_MESSAGES[code]);
        }
        this.name = 'AppError';
        this.code = code;
        this.httpStatus = ERROR_HTTP_STATUS[code];
        this.#fieldErrors = options.fieldErrors;
        this.#internalDetail = options.internalDetail;
    }
    /** Internal-only detail for structured logs; never part of any serialized form. */
    get internalDetail() {
        return this.#internalDetail;
    }
    get fieldErrors() {
        return this.#fieldErrors;
    }
    /** The exact wire envelope — nothing else ever crosses the API boundary. */
    toEnvelope() {
        return {
            code: this.code,
            message: this.message,
            ...(this.#fieldErrors === undefined ? {} : { fieldErrors: { ...this.#fieldErrors } }),
        };
    }
    /** `JSON.stringify(appError)` serializes the envelope only (no leak path). */
    toJSON() {
        return this.toEnvelope();
    }
}
/**
 * Map ANY thrown value to a safe envelope. Non-`AppError` values (including
 * native `Error`s carrying internal messages) collapse to a generic INTERNAL
 * envelope — their message is never forwarded.
 */
export function toErrorEnvelope(error) {
    if (error instanceof AppError) {
        return error.toEnvelope();
    }
    return { code: ERROR_CODES.INTERNAL, message: SAFE_ERROR_MESSAGES.INTERNAL };
}
/**
 * Flatten a `ZodError` into the envelope's `fieldErrors` map
 * (first issue per dotted path wins; issues with an empty path key as `_root`).
 */
export function fieldErrorsFromZod(error) {
    const out = {};
    for (const issue of error.issues) {
        const key = issue.path.length > 0 ? issue.path.map(String).join('.') : '_root';
        if (!(key in out)) {
            out[key] = issue.message;
        }
    }
    return out;
}
//# sourceMappingURL=errors.js.map